import React, { useState, useEffect } from 'react';
import { Outlet, NavLink, useLocation, useNavigate } from 'react-router-dom';
import {
  Home, Package, ShoppingBag, MoreHorizontal, ArrowLeft, Shield, LogOut, FolderTree,
} from 'lucide-react';
import { useApp } from '../../context/AppContext';
import { getPlatform } from '../../utils/platform';
import { normalizePhone, formatPhoneDisplay, isPhoneComplete } from '../../utils/phone';
import client from '../../api/client';
import { NAV_MAIN, NAV_SECONDARY } from './nav';

/* ── Навигация ─────────────────────────────────────────────────────────── */

// Состав разделов — в ./nav.js (общий с мини-аппом, см. NAV_MAIN/NAV_SECONDARY).

// Компактный ряд вкладок — для телефона (как в мини-аппе).
// Порядок — как в боковом меню вэб-версии (nav.js); выбор слотов —
// по просьбе владельца: Категории внизу, Клиенты в «Ещё».
const BOTTOM_TABS = [
  { path: '/admin', label: 'Главная', Icon: Home, end: true },
  { path: '/admin/orders', label: 'Заказы', Icon: ShoppingBag },
  { path: '/admin/products', label: 'Товары', Icon: Package },
  { path: '/admin/categories', label: 'Категории', Icon: FolderTree, ownerOnly: true },
  { path: '/admin/more', label: 'Ещё', Icon: MoreHorizontal },
];

const TITLES = {
  '/admin': 'Главная',
  '/admin/products': 'Товары',
  '/admin/categories': 'Категории',
  '/admin/orders': 'Заказы',
  '/admin/team': 'Команда',
  '/admin/users': 'Клиенты',
  '/admin/broadcast': 'Рассылки',
  '/admin/stats': 'Статистика',
  '/admin/subscription': 'Подписка',
  '/admin/settings': 'Настройки',
  '/admin/activity': 'Журнал изменений',
  '/admin/site-photos': 'Фото питомника',
  '/admin/notes': 'Заметки',
  '/admin/more': 'Ещё',
  '/admin/faq': 'Справка FAQ',
};

/* ── Вход с компьютера: телефон → мессенджер → код ─────────────────────── */

function WebLogin() {
  const { requestLoginCode, loginWithCode } = useApp();
  const [step, setStep] = useState('phone'); // phone | platform | code
  const [phone, setPhone] = useState('');
  const [platform, setPlatform] = useState('');
  const [code, setCode] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  const sendCode = async (plat) => {
    setError('');
    setBusy(true);
    try {
      await requestLoginCode(normalizePhone(phone), plat);
      setPlatform(plat);
      setStep('code');
    } catch (err) {
      const status = err.response?.status;
      if (status === 404) {
        setError('Не нашли аккаунт с таким телефоном. Откройте бота, нажмите /start — и повторите вход.');
      } else if (status === 429) {
        setError('Слишком много запросов кода. Подождите минуту.');
      } else {
        setError('Не удалось отправить код. Проверьте связь и попробуйте ещё раз.');
      }
    } finally {
      setBusy(false);
    }
  };

  const verify = async () => {
    setError('');
    setBusy(true);
    try {
      await loginWithCode(normalizePhone(phone), code.trim());
    } catch (err) {
      const status = err.response?.status;
      if (status === 400) setError('Неверный или просроченный код. Запросите новый.');
      else if (status === 404) setError('Аккаунт не найден. Откройте бота и нажмите /start.');
      else setError('Не удалось войти. Проверьте связь и попробуйте ещё раз.');
      setBusy(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-[#f1f5f9] px-4 py-10">
      <div className="w-full max-w-md">
        <div className="mb-6 text-center">
          <div className="mx-auto mb-3 grid h-12 w-12 place-items-center rounded-2xl bg-[#374151] text-white">
            <Shield className="h-6 w-6" />
          </div>
          <h1 className="text-xl font-bold text-[#0f172a]">Админ-панель So.Va</h1>
          <p className="mt-1 text-sm text-[#64748b]">
            Вход по коду из бота — Telegram или Max.
          </p>
        </div>

        <div className="space-y-4 rounded-2xl bg-white p-6 ring-1 ring-[#e2e8f0]">
          {step === 'phone' && (
            <>
              <div>
                <label className="mb-1 block text-xs font-medium text-[#64748b]" htmlFor="admin-phone">
                  Телефон
                </label>
                <input
                  id="admin-phone"
                  type="tel"
                  inputMode="tel"
                  autoComplete="tel"
                  placeholder="+7 (___) ___-__-__"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  onBlur={() => setPhone(prev => formatPhoneDisplay(prev))}
                  className="w-full rounded-xl border border-[#e2e8f0] px-4 py-3 text-sm outline-none focus:border-[#6B9B74]"
                />
              </div>
              <button
                type="button"
                disabled={busy || !isPhoneComplete(phone)}
                onClick={() => setStep('platform')}
                className="w-full rounded-xl bg-[#374151] px-5 py-3 text-sm font-semibold text-white transition hover:bg-[#1f2937] disabled:opacity-50"
              >
                Продолжить
              </button>
              <p className="text-xs text-[#94a3b8]">
                Мы отправим одноразовый код в бот — Telegram или Max.
              </p>
            </>
          )}

          {step === 'platform' && (
            <>
              <p className="text-sm text-[#64748b]">
                Телефон: <strong className="text-[#0f172a]">{formatPhoneDisplay(phone)}</strong>. Куда прислать код?
              </p>
              <div className="grid grid-cols-2 gap-3">
                <button
                  type="button"
                  disabled={busy}
                  onClick={() => sendCode('telegram')}
                  className="rounded-xl bg-[#229ED9] px-4 py-3 text-sm font-semibold text-white transition hover:brightness-105 disabled:opacity-50"
                >
                  Telegram
                </button>
                <button
                  type="button"
                  disabled={busy}
                  onClick={() => sendCode('max')}
                  className="rounded-xl bg-gradient-to-br from-[#471AFF] to-[#9500FF] px-4 py-3 text-sm font-semibold text-white transition hover:brightness-105 disabled:opacity-50"
                >
                  Max
                </button>
              </div>
              <button
                type="button"
                onClick={() => { setStep('phone'); setError(''); }}
                className="text-sm text-[#64748b] underline-offset-2 hover:underline"
              >
                ← Изменить телефон
              </button>
            </>
          )}

          {step === 'code' && (
            <>
              <p className="text-sm text-[#64748b]">
                Код отправлен в {platform === 'max' ? 'Max' : 'Telegram'} для номера{' '}
                <strong className="text-[#0f172a]">{formatPhoneDisplay(phone)}</strong>. Введите 6 цифр из сообщения бота.
              </p>
              <input
                autoFocus
                type="text"
                inputMode="numeric"
                maxLength={6}
                placeholder="······"
                autoComplete="one-time-code"
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                onKeyDown={(e) => { if (e.key === 'Enter' && code.length === 6) verify(); }}
                className="w-full rounded-xl border border-[#e2e8f0] px-4 py-3 text-center text-2xl tracking-[0.5em] outline-none focus:border-[#6B9B74]"
              />
              <button
                type="button"
                disabled={busy || code.length !== 6}
                onClick={verify}
                className="w-full rounded-xl bg-[#374151] px-5 py-3 text-sm font-semibold text-white transition hover:bg-[#1f2937] disabled:opacity-50"
              >
                {busy ? 'Входим…' : 'Войти'}
              </button>
              <button
                type="button"
                onClick={() => sendCode(platform)}
                className="text-sm text-[#64748b] underline-offset-2 hover:underline"
              >
                Отправить код ещё раз
              </button>
            </>
          )}

          {error && (
            <div className="rounded-xl bg-[#D64545]/10 px-4 py-3 text-sm text-[#D64545]">{error}</div>
          )}
        </div>

        <p className="mt-4 text-center text-xs text-[#94a3b8]">
          Код придёт в бота: откройте его и нажмите /start, если раньше не открывали.
        </p>
      </div>
    </div>
  );
}

/* ── Layout ────────────────────────────────────────────────────────────── */

export default function NewAdminLayout() {
  const { user, setUser, authReady } = useApp();
  const location = useLocation();
  const navigate = useNavigate();
  const [activeOrders, setActiveOrders] = useState(0);

  // Бейдж «активные заказы» на вкладке Заказы — и в сайдбаре, и в нижних вкладках
  useEffect(() => {
    client.get('/admin/dashboard/stats')
      .then((res) => setActiveOrders(Number(res.data?.active_orders_count) || 0))
      .catch(() => {});
  }, []);

  // Смена раздела — страница открывается сверху
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'auto' });
  }, [location.pathname]);

  const logout = () => {
    localStorage.removeItem('sova-token');
    setUser(null);
    // В браузере (web) — сразу на форму входа в админку; в боте — в магазин,
    // где сессия Mini App обрабатывается самим ботом
    navigate(getPlatform() === 'web' ? '/admin' : '/');
  };

  if (user === null) {
    // На компьютере: ждём проверку токена, потом — форма входа по коду.
    // authReady гарантирует, что при 401/ошибке сети мы не висим в спиннере.
    if (getPlatform() === 'web') {
      if (!authReady) {
        return (
          <div className="flex min-h-screen flex-col items-center justify-center gap-3">
            <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-[#6B9B74]" />
            <span className="text-xs text-[#64748b]">Входим...</span>
          </div>
        );
      }
      return <WebLogin />;
    }
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-3">
        <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-[#6B9B74]" />
        <span className="text-xs text-[#64748b]">Входим...</span>
      </div>
    );
  }

  const isFullAdmin = user.is_admin && (user.role === 'owner' || user.role === 'developer');
  const isManager = user.is_admin && user.role === 'manager';

  if (!isFullAdmin && !isManager) {
    // Вошли кодом, но номер не администратора — явный отказ вместо
    // тихого перехода в магазин (просьба пользователя 2026-11-02)
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-4 bg-[#f1f5f9] px-4 py-10">
        <div className="w-full max-w-md rounded-2xl bg-white p-6 text-center ring-1 ring-[#e2e8f0]">
          <div className="mx-auto mb-4 grid h-12 w-12 place-items-center rounded-2xl bg-[#D64545]/10 text-[#D64545]">
            <Shield className="h-6 w-6" />
          </div>
          <h1 className="text-lg font-bold text-[#0f172a]">В доступе отказано</h1>
          <p className="mt-2 text-sm leading-relaxed text-[#64748b]">
            Этот номер не принадлежит администратору — вход в панель доступен
            только сотрудникам. Если вы считаете, что это ошибка, обратитесь
            к владельцу.
          </p>
          <button
            type="button"
            onClick={logout}
            className="mt-5 w-full rounded-xl bg-[#374151] px-5 py-3 text-sm font-semibold text-white transition hover:bg-[#1f2937] active:scale-[0.98]"
          >
            Выйти
          </button>
        </div>
      </div>
    );
  }

  const mainItems = NAV_MAIN.filter((item) => !item.ownerOnly || isFullAdmin);
  const secondaryItems = NAV_SECONDARY.filter((item) => !item.ownerOnly || isFullAdmin);
  const visibleTabs = BOTTOM_TABS.filter((tab) => !tab.ownerOnly || isFullAdmin);

  const title = Object.entries(TITLES).find(([path]) =>
    location.pathname === path || (path !== '/admin' && location.pathname.startsWith(path))
  )?.[1] || 'Админ';

  // Главные = нижние вкладки (телефон) и верхние пункты сайдбара (веб):
  // у них кнопки «Назад» нет. Остальные разделы (Клиенты, Рассылки, Статистика…)
  // открываются «внутри» — с кнопкой «Назад», как просил владелец.
  const mainPaths = BOTTOM_TABS.map((tab) => tab.path);
  const showBack = !mainPaths.some(
    (p) => location.pathname === p || (p !== '/admin' && location.pathname.startsWith(p + '/'))
  );

  const orderBadge = (path) =>
    path === '/admin/orders' && activeOrders > 0 ? (
      <span className="ml-auto rounded-full bg-[#D64545] px-1.5 py-0.5 text-[10px] font-semibold leading-none text-white">
        {activeOrders > 99 ? '99+' : activeOrders}
      </span>
    ) : null;

  const renderNavLink = ({ path, label, Icon, end }) => (
    <NavLink
      key={path}
      to={path}
      end={end}
      className={({ isActive }) =>
        `flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium transition ${
          isActive
            ? 'bg-[#374151] text-white'
            : 'text-[#64748b] hover:bg-[#f1f5f9] hover:text-[#0f172a]'
        }`
      }
    >
      <Icon className="h-[18px] w-[18px]" />
      <span>{label}</span>
      {orderBadge(path)}
    </NavLink>
  );

  return (
    <div className="flex min-h-screen bg-[#f1f5f9]" style={{ overscrollBehavior: 'none' }}>
      {/* Боковое меню — компьютер */}
      <aside className="fixed inset-y-0 left-0 z-30 hidden w-60 flex-col border-r border-[#e2e8f0] bg-white md:flex">
        <button
          type="button"
          onClick={() => {
            navigate('/admin');
            window.scrollTo({ top: 0, behavior: 'auto' });
          }}
          className="flex w-full items-center gap-2.5 border-b border-[#e2e8f0] px-5 py-4 text-left"
        >
          <Shield className="h-5 w-5 text-[#374151]" />
          <div>
            <div className="text-sm font-bold text-[#0f172a]">So.Va — админка</div>
            <div className="text-[11px] text-[#94a3b8]">{user.first_name || 'Администратор'}</div>
          </div>
        </button>

        {/* Основное — ежедневная работа */}
        <nav className="flex-1 space-y-0.5 overflow-y-auto px-3 py-3">
          {mainItems.map(renderNavLink)}
        </nav>

        {/* Сайт и администрирование — нижний блок (по рисунку пользователя) */}
        <div className="border-t border-[#e2e8f0] px-3 py-3">
          <div className="px-3 pb-1.5 text-[11px] font-semibold uppercase tracking-wide text-[#94a3b8]">
            Сайт и администрирование
          </div>
          <div className="space-y-0.5">
            {secondaryItems.map(renderNavLink)}
          </div>
        </div>

        <div className="border-t border-[#e2e8f0] p-3">
          <button
            type="button"
            onClick={logout}
            className="flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium text-[#D64545] transition hover:bg-[#D64545]/10"
          >
            <LogOut className="h-[18px] w-[18px]" />
            <span>Выйти</span>
          </button>
        </div>
      </aside>

      {/* Основная колонка. min-w-0 обязателен: без него flex-элемент не может
          сжаться меньше min-content длинных строк (truncate) и расталкивает
          страницу по горизонтали. */}
      <div className="flex min-h-screen min-w-0 flex-1 flex-col md:pl-60">
        {/* Шапка */}
        <header className="sticky top-0 z-30 flex items-center gap-3 border-b border-[#e2e8f0] bg-white/90 px-4 py-3 backdrop-blur-xl">
          {showBack && (
            <button
              onClick={() => navigate(-1)}
              className="grid h-8 w-8 place-items-center rounded-lg bg-[#f1f5f9] text-[#64748b]"
              aria-label="Назад"
            >
              <ArrowLeft className="h-4 w-4" />
            </button>
          )}
          <Shield className="h-5 w-5 text-[#374151] md:hidden" />
          <h1 className="text-lg font-bold text-[#0f172a]">{title}</h1>
        </header>

        {/* Контент */}
        <main className="flex-1 px-4 py-4 pb-24 md:px-8 md:py-6 md:pb-10">
          <div className="mx-auto w-full max-w-md md:max-w-5xl">
            <Outlet />
          </div>
        </main>
      </div>

      {/* Нижняя навигация — телефон */}
      <nav className="fixed bottom-0 left-0 right-0 z-40 border-t border-[#e2e8f0] bg-white/95 backdrop-blur-xl md:hidden">
        <div className={`mx-auto grid max-w-md ${visibleTabs.length === 4 ? 'grid-cols-4' : 'grid-cols-5'}`}>
          {visibleTabs.map(({ path, label, Icon, end }) => (
            <NavLink
              key={path}
              to={path}
              end={end}
              className={({ isActive }) =>
                `flex flex-col items-center gap-1 py-2.5 text-xs font-medium transition ${
                  isActive ? 'text-[#374151]' : 'text-[#64748b]'
                }`
              }
            >
              <span className="relative">
                <Icon className="h-6 w-6" />
                {path === '/admin/orders' && activeOrders > 0 && (
                  <span className="absolute -right-2.5 -top-1 min-w-[16px] rounded-full bg-[#D64545] px-1 text-center text-[10px] font-semibold leading-4 text-white">
                    {activeOrders > 99 ? '99+' : activeOrders}
                  </span>
                )}
              </span>
              <span>{label}</span>
            </NavLink>
          ))}
        </div>
      </nav>
    </div>
  );
}
